import type {
  FeedMapping,
  FeedMappingOverride,
  FeedMappingFallback,
  FeedMappingDraftSummary,
  GetFeedMappingResponse,
  SaveFeedMappingResponse,
} from '@/types/feedMapping'

export type { FeedMappingDraftSummary } from '@/types/feedMapping'

export async function fetchFeedMapping(
  endpoint: string,
  jazelId: string,
  opts?: { token?: string },
): Promise<GetFeedMappingResponse> {
  const base = String(endpoint || '').replace(/\/+$/g, '')
  const url = new URL(`${base}/ise-settings/${encodeURIComponent(jazelId)}`)
  url.searchParams.set('action', 'feedMapping')

  const headers: Record<string, string> = {}
  const token = String(opts?.token || '').trim()
  if (token) headers.Authorization = `Bearer ${token}`

  const resp = await fetch(url.toString(), { headers })
  if (!resp.ok) throw new Error(`Failed to load feed mapping (${resp.status})`)
  const json = (await resp.json().catch(() => ({}))) as any
  const data = (json?.data || {}) as GetFeedMappingResponse
  return {
    ...data,
    overrides: Array.isArray(data.overrides) ? data.overrides : [],
    fallbacks: Array.isArray(data.fallbacks) ? data.fallbacks : [],
    derivedPrimarySources: Array.isArray(data.derivedPrimarySources) ? data.derivedPrimarySources : [],
  }
}

/**
 * Reads the server-computed Feed Source Config draft summary (matrix + overrides +
 * fallbacks diffed against published). Used by Unpublished Changes to render Feed
 * Source Config as its own section, independent of primarySources.
 */
export async function fetchFeedMappingDraftSummary(
  endpoint: string,
  jazelId: string,
  opts?: { token?: string },
): Promise<FeedMappingDraftSummary> {
  const base = String(endpoint || '').replace(/\/+$/g, '')
  const url = new URL(`${base}/ise-settings/${encodeURIComponent(jazelId)}`)
  url.searchParams.set('action', 'feedMappingDraftSummary')

  const headers: Record<string, string> = {}
  const token = String(opts?.token || '').trim()
  if (token) headers.Authorization = `Bearer ${token}`

  const resp = await fetch(url.toString(), { headers })
  if (!resp.ok) throw new Error(`Failed to load feed mapping draft summary (${resp.status})`)
  const json = (await resp.json().catch(() => ({}))) as any
  const data = (json?.data || {}) as FeedMappingDraftSummary
  return {
    changed: !!data.changed,
    savedAt: data.savedAt,
    savedBy: data.savedBy,
    diff: data.diff,
  }
}

export async function saveFeedMapping(
  endpoint: string,
  jazelId: string,
  feedMapping: FeedMapping,
  overrides: FeedMappingOverride[],
  fallbacks: FeedMappingFallback[],
  opts?: { token?: string },
): Promise<SaveFeedMappingResponse> {
  const base = String(endpoint || '').replace(/\/+$/g, '')
  const url = new URL(`${base}/ise-settings/${encodeURIComponent(jazelId)}`)
  url.searchParams.set('action', 'feedMapping')

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = String(opts?.token || '').trim()
  if (token) headers.Authorization = `Bearer ${token}`

  const resp = await fetch(url.toString(), {
    method: 'PUT',
    headers,
    body: JSON.stringify({ feedMapping, overrides, fallbacks }),
  })

  if (!resp.ok) {
    const body = await resp.json().catch(() => null)
    const messages = body?.messages ?? [body?.message ?? `Failed to save feed mapping (${resp.status})`]
    throw new Error(messages.join('; '))
  }

  const json = (await resp.json().catch(() => ({}))) as any
  return (json?.data || {}) as SaveFeedMappingResponse
}
